import {
  QuestionFilterCount,
  QuestionStatus,
} from 'router/subrouters/DataRoom/models/QAndA';
import { safeStringify } from 'utils';

export const Q_AND_A_QUESTION_STATUS = {
  DRAFTED: 'drafted', //only on buy side

  SELL_SIDE_ASIGNED: 'sell_side_assigned',
  ANSWER_REJECTED: 'answer_rejected',
  QUESTION_REJECTED: 'question_rejected',
  ANSWERED: 'answered',
  ANSWER_APPROVED: 'answer_approved',

  QUESTION_APPROVED: 'question_approved', //only expert doesnt see this.
} as const;

const QUERY_FILTER_KEYWORD = 'status';

const prepareQuery = (filters: string[]) =>
  safeStringify(
    filters.map((filter) => ({
      [QUERY_FILTER_KEYWORD]: filter,
    })),
  );

const BUY_SIDE_ARRAY_KEYS = [
  'IN_REVIEW_BUY_SIDE',
  'AWAITING_ANSWER_BUY_SIDE',
  'ANSWERED_BUY_SIDE',
] as Array<keyof typeof QUESTION_QUERY_ARRAYS>;

const MAP_ARRAY_TO_COPY = {
  IN_REVIEW_BUY_SIDE: 'inReview',
  AWAITING_ANSWER_BUY_SIDE: 'awaitingAnswer',
  ANSWERED_BUY_SIDE: 'answered',
};

export const QUESTION_QUERY_ARRAYS_BUY_SIDE: Record<
  string,
  Array<QuestionStatus>
> = {
  IN_REVIEW_BUY_SIDE: [
    Q_AND_A_QUESTION_STATUS.DRAFTED,
    Q_AND_A_QUESTION_STATUS.QUESTION_REJECTED,
  ],
  AWAITING_ANSWER_BUY_SIDE: [
    Q_AND_A_QUESTION_STATUS.QUESTION_APPROVED,
    Q_AND_A_QUESTION_STATUS.ANSWERED,
    Q_AND_A_QUESTION_STATUS.ANSWER_REJECTED,
    Q_AND_A_QUESTION_STATUS.SELL_SIDE_ASIGNED,
  ],
  ANSWERED_BUY_SIDE: [Q_AND_A_QUESTION_STATUS.ANSWER_APPROVED],
};

export const QUESTION_QUERY_ARRAYS_SELL_SIDE: Record<
  string,
  Array<QuestionStatus>
> = {
  NEEDS_APPROVAL_SELL_SIDE: [Q_AND_A_QUESTION_STATUS.ANSWERED],
  PUBLISHED_SELL_SIDE: [Q_AND_A_QUESTION_STATUS.ANSWER_APPROVED],
  UNANSWERED: [
    Q_AND_A_QUESTION_STATUS.ANSWER_REJECTED,
    Q_AND_A_QUESTION_STATUS.SELL_SIDE_ASIGNED,
    Q_AND_A_QUESTION_STATUS.QUESTION_APPROVED,
  ],
};

export const QUESTION_QUERY_ARRAYS: Record<string, Array<QuestionStatus>> = {
  ...QUESTION_QUERY_ARRAYS_BUY_SIDE,
  ...QUESTION_QUERY_ARRAYS_SELL_SIDE,
};

//seller
export const NEEDS_APPROVAL_QUERY = prepareQuery(
  QUESTION_QUERY_ARRAYS.NEEDS_APPROVAL_SELL_SIDE,
);
export const PUBLISHED_QUERY = prepareQuery(
  QUESTION_QUERY_ARRAYS.PUBLISHED_SELL_SIDE,
);

export const UNANSWERED_SELL_SIDE_QUERY = prepareQuery(
  QUESTION_QUERY_ARRAYS.UNANSWERED,
);

//buy
export const IN_REVIEW_QUERY_BUY_SIDE = prepareQuery(
  QUESTION_QUERY_ARRAYS.IN_REVIEW_BUY_SIDE,
);
export const AWAITING_ANSWER_QUERY = prepareQuery(
  QUESTION_QUERY_ARRAYS.AWAITING_ANSWER_BUY_SIDE,
);
export const ANSWERED_QUERY = prepareQuery(
  QUESTION_QUERY_ARRAYS.ANSWERED_BUY_SIDE,
);

export const getQAParams = (
  params: Record<string, Array<QuestionStatus>>,
  value: QuestionStatus,
) =>
  encodeURIComponent(
    prepareQuery(Object.values(params).find((el) => el.includes(value)) || []),
  );

export const getQASellSideParams = (value: QuestionStatus) =>
  getQAParams(QUESTION_QUERY_ARRAYS_SELL_SIDE, value);

export const getQABuySideParams = (value: QuestionStatus) =>
  getQAParams(QUESTION_QUERY_ARRAYS_BUY_SIDE, value);

export const FilterCopies = {
  IN_REVIEW_BUY: 'inReviewBuy',
  NEEDS_APPROVAL: 'needsApproval',
  PUBLISHED: 'published',
  AWAITING_ANSWER: 'awaitingAnsewer',
  ANSWERED: 'answered',
  UNANSWERED: 'unanswered',
};

export const FILTER_COPIES_TO_COUNT_MAP: Record<
  typeof FilterCopies[keyof typeof FilterCopies],
  keyof QuestionFilterCount
> = {
  needsApproval: 'needsApprovalCount',
  published: 'publishedCount',
  inReviewBuy: 'inReviewCount',
  awaitingAnsewer: 'awaitingAnswerCount',
  answered: 'answeredCount',
  unanswered: 'unanswered',
  all: 'allCount',
};

export const getCurrentBuyStateByStatus = (status: QuestionStatus) => {
  const selectedState = BUY_SIDE_ARRAY_KEYS.find((key) =>
    QUESTION_QUERY_ARRAYS[key as keyof typeof QUESTION_QUERY_ARRAYS].some(
      (el) => el === status,
    ),
  );

  return (
    MAP_ARRAY_TO_COPY?.[selectedState as keyof typeof MAP_ARRAY_TO_COPY] || ''
  );
};

export const COMMON_FILTERS = [
  {
    label: 'all',
    value: '',
    fval: 'all',
  },
];

export const BUYER_SIDE_FILTERS = [
  ...COMMON_FILTERS,
  {
    label: 'inReview',
    value: IN_REVIEW_QUERY_BUY_SIDE,
    fval: FilterCopies.IN_REVIEW_BUY,
  },
  {
    label: 'awaitingAnswer',
    value: AWAITING_ANSWER_QUERY,
    fval: FilterCopies.AWAITING_ANSWER,
  },
  {
    label: 'anwered',
    value: ANSWERED_QUERY,
    fval: FilterCopies.ANSWERED,
  },
];

export const SELLER_SIDE_FILTERS = [
  ...COMMON_FILTERS,
  {
    label: 'unanswered',
    value: UNANSWERED_SELL_SIDE_QUERY,
    fval: FilterCopies.UNANSWERED,
  },

  {
    label: 'needsApproval',
    value: NEEDS_APPROVAL_QUERY,
    fval: FilterCopies.NEEDS_APPROVAL,
  },

  {
    label: 'published',
    value: PUBLISHED_QUERY,
    fval: FilterCopies.PUBLISHED,
  },
];

export const FITLER_PARAMS_TO_EMPTY_STATE: Record<string, string> = {
  [ANSWERED_QUERY]: 'answeredEmpty',
  [AWAITING_ANSWER_QUERY]: 'awaitingAnsewerEmpty',
  [IN_REVIEW_QUERY_BUY_SIDE]: 'inReviewBuyEmpty',
  [PUBLISHED_QUERY]: 'publishedEmpty',
  [UNANSWERED_SELL_SIDE_QUERY]: 'unansweredEmpty',
  [NEEDS_APPROVAL_QUERY]: 'needApprovalEmpty',
};
